import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import axios from '../lib/axios';
import { endpoints } from '../constants/endpoints';
import type { Chatbot, ChatbotUpdate } from '../types';

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ChatbotUpdate>();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data: Chatbot = await axios.get(endpoints.chatbot);
        reset({ name: data.name, personality: data.personality });
      } catch {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [reset]);

  const onSubmit = async (data: ChatbotUpdate) => {
    setSaving(true);
    try {
      await axios.put(endpoints.chatbot, data);
      toast.success('Settings saved. Restart the server to apply changes.');
      reset(data);
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen flex-col">
        <div className="border-b border-gray-200 bg-white px-6 py-3">
          <h1 className="text-lg font-semibold text-gray-900">Settings</h1>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="text-gray-500">Loading settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      <div className="border-b border-gray-200 bg-white px-6 py-3">
        <h1 className="text-lg font-semibold text-gray-900">Settings</h1>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-2xl">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <h2 className="text-base font-medium text-gray-900 mb-4">
                Chatbot Configuration
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Configure how the chatbot introduces itself and behaves.
              </p>
            </div>

            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Bot Name
              </label>
              <input
                id="name"
                type="text"
                {...register('name', { required: 'Bot name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., ASSA Bot"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.name.message}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                The name the bot uses to introduce itself
              </p>
            </div>

            <div>
              <label
                htmlFor="personality"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Personality
              </label>
              <textarea
                id="personality"
                rows={4}
                {...register('personality', {
                  required: 'Personality is required',
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Be friendly, helpful and professional."
              />
              {errors.personality && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.personality.message}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Describes the tone and style of the bot's responses
              </p>
            </div>

            <div className="flex items-center gap-4 pt-4">
              <button
                type="submit"
                disabled={saving || !isDirty}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              {isDirty && (
                <span className="text-sm text-gray-500">Unsaved changes</span>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
